(ns hai.journal2.events
  "Integration page re-frame events, subscriptions, and query helpers."
  (:require
   [clojure.string :as str]
   [hai.contacts.create :as create]
   [hai.journal2.integration-registry :as reg]
   [hai.ui.rf-sugar :refer [>e defevent defevent-fx defsub]]
   [lambdaisland.deja-fu :as fu]))

;; ---------------------------------------------------------------------------
;; Query helpers
;; ---------------------------------------------------------------------------

(defn journal-entry-query []
  (let [? #(list % {:com.wsscode.pathom3.connect.operation/optional? true})]
    [:journal.entry/uuid
     :journal.entry/time
     :journal.entry/created-at
     {:journal.entry/type [:db/ident]}
     :timeline-integration/uuid
     (? :journal.entry/author-display-name)
     (? :journal.entry/creator-email)
     ;; google-calendar
     (? :journal.entry.google-calendar/source-id)
     (? :journal.entry.google-calendar/title)
     (? :journal.entry.google-calendar/description)
     (? :journal.entry.google-calendar/end-at)
     (? :journal.entry.google-calendar/attendees)
     (? :journal.entry.google-calendar/attendee-names)
     (? :journal.entry.google-calendar/link)
     (? :journal.entry.google-calendar/meet-link)
     ;; outlook-calendar
     (? :journal.entry.outlook-calendar/source-id)
     (? :journal.entry.outlook-calendar/title)
     (? :journal.entry.outlook-calendar/description)
     (? :journal.entry.outlook-calendar/end-at)
     (? :journal.entry.outlook-calendar/attendees)
     (? :journal.entry.outlook-calendar/attendee-names)
     (? :journal.entry.outlook-calendar/link)
     (? :journal.entry.outlook-calendar/meet-link)
     ;; linear
     (? :journal.entry.linear/linear-id)
     (? {:journal.entry.linear/source-type [:db/ident]})
     (? :journal.entry.linear/title)
     (? :journal.entry.linear/description)
     (? :journal.entry.linear/link)
     (? {:journal.entry.linear/event-type [:db/ident]})
     (? :journal.entry.linear/team-name)
     (? :journal.entry.linear/identifier)
     (? :journal.entry.linear/priority-label)
     (? :journal.entry.linear/actor-email)
     (? :journal.entry.linear/creator-email)
     (? :journal.entry.linear/assignee-email)
     ;; github
     (? :journal.entry.github/github-id)
     (? {:journal.entry.github/source-type [:db/ident]})
     (? {:journal.entry.github/event-type [:db/ident]})
     (? :journal.entry.github/title)
     (? :journal.entry.github/link)
     (? :journal.entry.github/repo-name)
     (? :journal.entry.github/pr-number)
     (? :journal.entry.github/actor-email)
     (? :journal.entry.github/actor-login)
     (? :journal.entry.github/author-email)
     (? :journal.entry.github/author-login)
     (? :journal.entry.github/reviewer-email)
     (? :journal.entry.github/reviewer-login)
     ;; slack
     (? :journal.entry.slack-dm/id)
     (? :journal.entry.slack-dm/uuid)
     (? :journal.entry.slack-dm/team-id)
     (? :journal.entry.slack-dm/team-domain)
     (? :journal.entry.slack-dm/app-id)
     (? :journal.entry.slack-dm/channel-id)
     (? :journal.entry.slack-dm/sender-id)
     (? :journal.entry.slack-dm/sender)
     (? :journal.entry.slack-dm/text)
     (? :journal.entry.slack-dm/attachments)
     ;; notes (virtualised from :note/text history)
     (? :journal.entry.notes/text)
     (? :journal.entry.notes/note-uuid)
     (? :journal.entry.notes/url)
     (? :journal.entry.notes/tags)
     ;; tasks (virtualised from :task/created-at and :task/completed-at)
     (? {:journal.entry.tasks/event-type [:db/ident]})
     (? :journal.entry.tasks/title)
     (? :journal.entry.tasks/description)
     (? :journal.entry.tasks/task-uuid)]))

(def ^:private all-time-chunk-days
  "Initial window + load-more step (in days) for the :all-time preset."
  7)

(def ^:private min-initial-fill
  "Minimum entries the :all-time view crawls back to load before settling. A sparse
  recent window (e.g. only today's calendar events) would otherwise leave too short
  a page to create a scroll region, so the infinite-scroll sentinel never fires and
  the user is stuck seeing only the few recent entries. Crawling to this count
  guarantees the viewport fills and older history is reachable."
  15)

(defn- date-preset->range
  "Converts a date preset keyword to a {:since :until} map of js/Date.
  Bounded presets extend :until to the end of their named period in local
  time, so e.g. :today includes meetings scheduled later today and :this-week
  includes the rest of the week."
  [preset {:keys [custom-since custom-until]}]
  (let [now (js/Date.)
        start-of-day  (fn [d] (doto (js/Date. (.getTime d))
                                (.setHours 0 0 0 0)))
        end-of-day    (fn [d] (doto (js/Date. (.getTime d))
                                (.setHours 23 59 59 999)))
        end-of-week   (fn [d]
                        (let [s (start-of-day d)]
                          (.setDate s (+ (- (.getDate s) (.getDay s)) 6))
                          (end-of-day s)))
        end-of-month  (fn [d]
                        (js/Date. (.getFullYear d)
                                  (inc (.getMonth d))
                                  0 23 59 59 999))]
    (case preset
      :today      {:since (start-of-day now) :until (end-of-day now)}
      :this-week  {:since (let [d (start-of-day now)]
                            (.setDate d (- (.getDate d) (.getDay d)))
                            d)
                   :until (end-of-week now)}
      :this-month {:since (let [d (start-of-day now)]
                            (.setDate d 1) d)
                   :until (end-of-month now)}
      :last-30    {:since (let [d (js/Date.)] (.setDate d (- (.getDate d) 30)) d)
                   :until (end-of-day now)}
      :custom     (cond-> {}
                    (seq custom-since)
                    (assoc :since (js/Date. (str custom-since "T00:00:00")))
                    (seq custom-until)
                    (assoc :until (doto (js/Date. (str custom-until "T23:59:59"))
                                    (.setMilliseconds 999))))
      :all-time   {:since (let [d (js/Date.)]
                            (.setDate d (- (.getDate d) all-time-chunk-days))
                            d)
                   :until (end-of-day now)}
      {})))

(def integration-type-label
  "Derives a display name from a timeline-integration type ident."
  reg/integration-type-label)

;; ---------------------------------------------------------------------------
;; Auto-save debounce atom
;; ---------------------------------------------------------------------------

(defonce ^:private auto-save-handle (atom nil))

;; ---------------------------------------------------------------------------
;; Events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/init [{:keys [db]} _]
  ;; Restore saved page-filters FIRST (via the integration/saved-filters
  ;; resolver — deterministic, unlike reading :auth/identity which loads
  ;; async at boot and races init). Only once filters are seeded do we kick
  ;; off the connection-status -> available -> load-entries chain, so the
  ;; first entry fetch uses the restored filters.
  {:db (assoc db :timeline/loading? true)
   :fx [[:dispatch [:timeline/load-saved-filters]]
        [:dispatch [:timeline/load-people-directory]]
        [:dispatch [:timeline/load-contact-directory]]]})

(defevent-fx :timeline/load-saved-filters [_ _]
  {:resolve {:query [:integration/saved-filters]
             :store-in [:timeline/saved-filters-raw]
             :on-success-fx [[:dispatch [:timeline/saved-filters-loaded]]]
             :on-error-fx [[:dispatch [:timeline/saved-filters-loaded]]]}})

(defevent-fx :timeline/saved-filters-loaded [{:keys [db]} _]
  (let [saved (get-in db [:timeline/saved-filters-raw :integration/saved-filters])
        ;; Back-compat: legacy blobs were a bare page-filters map
        ;; ({:shown-types .. :date-range ..}); new blobs nest both dimensions.
        new-shape? (or (contains? saved :page-filters)
                       (contains? saved :entity-filter))
        page-filters  (if new-shape? (:page-filters saved) saved)
        entity-filter (when new-shape? (:entity-filter saved))]
    {:db (cond-> (dissoc db :timeline/saved-filters-raw)
           page-filters  (assoc :timeline/page-filters page-filters)
           entity-filter (assoc :timeline/entity-filter entity-filter))
     :fx [[:dispatch [:timeline/load-connection-status]]
          [:dispatch [:timeline/load-available]]
          [:dispatch [:timeline/refresh-gcal]]
          [:dispatch [:timeline/refresh-outlook]]]}))

(defevent-fx :timeline/load-available [_ _]
  {:resolve {:query [{:integrations/available
                      [:timeline-integration/uuid
                       {:timeline-integration/type [:db/ident]}
                       :timeline-integration/connected?
                       :timeline-integration/owned?]}]
             :store-in [:timeline/available-raw]
             :on-success-fx [[:dispatch [:timeline/available-loaded]]]}})

(defevent-fx :timeline/available-loaded [{:keys [db]} _]
  (let [raw (get-in db [:timeline/available-raw :integrations/available] [])]
    {:db (-> db
             (assoc :timeline/available raw)
             (dissoc :timeline/available-raw))
     :fx [[:dispatch [:timeline/load-entries]]]}))

(defevent-fx :timeline/load-entries [{:keys [db]} _]
  (let [page-filters (get db :timeline/page-filters
                          {:shown-types reg/all-types
                           :date-range {:preset :all-time}})
        ;; Build date range from preset
        preset     (get-in page-filters [:date-range :preset] :all-time)
        {:keys [since until]} (date-preset->range
                               preset
                               {:custom-since (get-in page-filters [:date-range :since])
                                :custom-until (get-in page-filters [:date-range :until])})
        ;; Build the server-side filter param
        page-param (cond-> {}
                     (seq (:shown-types page-filters))
                     (assoc :shown-types (:shown-types page-filters))
                     since (assoc-in [:date-range :since] since)
                     until (assoc-in [:date-range :until] until))
        prev-id    (:timeline/entries-request-id db)
        req-id     (random-uuid)]
    {:db (cond-> (-> db
                     (dissoc :timeline/error?)
                     (assoc :timeline/entries-request-id req-id))
           prev-id (update :resolve/canceled-requests (fnil conj #{}) prev-id))
     :resolve {:request-id req-id
               :query [{(list :integration/entry-view
                              {:selected-integrations nil
                               :filters {:page page-param}})
                        (journal-entry-query)}
                       :integration/has-more-history?]
               :store-in [:timeline/entries-raw]
               :on-success-fx [[:dispatch [:timeline/entries-loaded]]]
               :on-error-fx [[:dispatch [:timeline/load-error]]]}}))

(defevent-fx :timeline/entries-loaded [{:keys [db]} _]
  (let [raw       (get db :timeline/entries-raw {})
        entries   (get raw :integration/entry-view [])
        has-more? (get raw :integration/has-more-history? false)
        preset    (get-in db [:timeline/page-filters :date-range :preset] :all-time)
        ;; For :all-time the initial window covers the last
        ;; all-time-chunk-days. Seed the cursor at the start of that window
        ;; (= now - chunk-days) so the next load-more chunk picks up where
        ;; the initial fetch left off, regardless of whether the window was
        ;; empty or full.
        cursor    (when (= preset :all-time)
                    (let [d (js/Date.)]
                      (.setDate d (- (.getDate d) all-time-chunk-days))
                      d))]
    {:db (-> db
             (dissoc :timeline/entries-request-id :timeline/entries-raw)
             (assoc :timeline/entries entries
                    :timeline/loading? false
                    :timeline/all-loaded? (not has-more?)
                    :timeline/loading-more? false)
             (cond-> cursor (assoc :timeline/load-more-cursor cursor)))
     :fx (cond-> [[:dispatch [:timeline/check-backfill-on-load]]]
           ;; Initial all-time window came back too sparse to fill the viewport
           ;; but the backend has older entries — start crawling backwards so the
           ;; user isn't stuck on a short page with nothing to scroll-trigger
           ;; load-more (e.g. only today's calendar events landed in the window).
           ;; When the backend reports no more history, the :all-loaded? flag
           ;; above terminates the crawl.
           (and cursor has-more? (< (count entries) min-initial-fill))
           (conj [:dispatch [:timeline/load-more]]))}))

(defevent-fx :timeline/refresh-gcal [{:keys [db]} _]
  {:db (assoc db :timeline/refreshing? true)
   :resolve {:query [{(list 'integration/refresh-gcal {})
                      [:refresh-required?]}]
             :store-in [:timeline/refresh-result]
             :transform-fn (fn [resp] (get resp 'integration/refresh-gcal))
             :on-success-fx [[:dispatch [:timeline/refresh-done]]]}})

(defevent-fx :timeline/refresh-outlook [{:keys [db]} _]
  {:db (assoc db :timeline/refreshing? true)
   :resolve {:query [{(list 'integration/refresh-outlook-calendar {})
                      [:refresh-required?]}]
             :store-in [:timeline/refresh-result]
             :transform-fn (fn [resp] (get resp 'integration/refresh-outlook-calendar))
             :on-success-fx [[:dispatch [:timeline/refresh-done]]]}})

(defevent-fx :timeline/refresh-done [{:keys [db]} _]
  (let [result (get db :timeline/refresh-result)
        refresh? (:refresh-required? result)]
    (cond-> {:db (-> db
                     (assoc :timeline/refreshing? false)
                     (dissoc :timeline/refresh-result))}
      ;; Reload the currently-loaded span (NOT load-entries, which would collapse
      ;; the timeline back to the initial 7-day window and discard the user's
      ;; scroll depth — visible as history flashing in then vanishing).
      refresh?
      (assoc :fx [[:dispatch [:timeline/reload-current-window]]]))))

(defevent-fx :timeline/reload-current-window [{:keys [db]} _]
  ;; Re-fetch the entire currently-loaded span — from the initial window start
  ;; through the furthest-back point the user has scrolled to (load-more-cursor)
  ;; — and replace entries. Picks up new/retracted entries from a refresh sync
  ;; WITHOUT resetting scroll depth. Preserves the cursor so further load-more
  ;; continues from where the user was.
  (let [page-filters (get db :timeline/page-filters
                          {:shown-types reg/all-types
                           :date-range {:preset :all-time}})
        preset       (get-in page-filters [:date-range :preset] :all-time)
        {:keys [since until]} (date-preset->range
                               preset
                               {:custom-since (get-in page-filters [:date-range :since])
                                :custom-until (get-in page-filters [:date-range :until])})
        cursor       (get db :timeline/load-more-cursor)
        ;; For :all-time, load from the deepest point already shown rather than
        ;; the default 7-day start, so scrolled-in history survives the reload.
        eff-since    (if (and (= preset :all-time) cursor since
                              (< (.getTime cursor) (.getTime since)))
                       cursor
                       since)
        page-param   (cond-> {}
                       (seq (:shown-types page-filters))
                       (assoc :shown-types (:shown-types page-filters))
                       eff-since (assoc-in [:date-range :since] eff-since)
                       until     (assoc-in [:date-range :until] until))
        prev-id      (:timeline/entries-request-id db)
        req-id       (random-uuid)]
    {:db (cond-> (-> db
                     (dissoc :timeline/error?)
                     (assoc :timeline/entries-request-id req-id))
           prev-id (update :resolve/canceled-requests (fnil conj #{}) prev-id))
     :resolve {:request-id req-id
               :query [{(list :integration/entry-view
                              {:selected-integrations nil
                               :filters {:page page-param}})
                        (journal-entry-query)}
                       :integration/has-more-history?]
               :store-in [:timeline/entries-raw]
               :on-success-fx [[:dispatch [:timeline/reload-window-loaded]]]
               :on-error-fx [[:dispatch [:timeline/load-error]]]}}))

(defevent :timeline/reload-window-loaded [db _]
  ;; Like entries-loaded, but does NOT re-seed load-more-cursor — the reload
  ;; covered the already-loaded depth, so the cursor (and scroll position) stay.
  (let [raw       (get db :timeline/entries-raw {})
        entries   (get raw :integration/entry-view [])
        has-more? (get raw :integration/has-more-history? false)]
    (-> db
        (dissoc :timeline/entries-request-id :timeline/entries-raw)
        (assoc :timeline/entries entries
               :timeline/loading? false
               :timeline/all-loaded? (not has-more?)
               :timeline/loading-more? false))))

;; ---------------------------------------------------------------------------
;; People directory
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/load-people-directory [_ _]
  {:resolve {:query [:integration/people-directory]
             :store-in [:timeline/people-directory-raw]
             :on-success-fx [[:dispatch [:timeline/people-directory-loaded]]]}})

(defevent :timeline/people-directory-loaded [db _]
  (let [raw (get-in db [:timeline/people-directory-raw :integration/people-directory] [])
        ;; {any-identifier -> canonical-person} for O(1) lookup
        people-index
        (reduce (fn [idx {:keys [emails github-login] :as person}]
                  (cond-> (reduce #(assoc %1 (str/lower-case %2) person) idx emails)
                    github-login (assoc (str/lower-case github-login) person)))
                {}
                raw)
        ;; {canonical-id -> #{all-identifiers}} for reverse expansion during filtering
        canonical-index
        (reduce (fn [idx {:keys [id emails github-login]}]
                  (assoc idx id
                         (cond-> (into #{} (map str/lower-case) emails)
                           github-login (conj (str/lower-case github-login)))))
                {}
                raw)]
    (-> db
        (assoc :timeline/people-directory raw
               :timeline/people-index people-index
               :timeline/people-canonical-index canonical-index)
        (dissoc :timeline/people-directory-raw))))

;; Contact directory — the user's own contact-book entries. Indexed by every
;; identifier (lowercased email or github-login) so filter rows can be flagged
;; as :contact? true when they resolve to a real contact.
(defevent-fx :timeline/load-contact-directory [_ _]
  (let [? #(list % {:com.wsscode.pathom3.connect.operation/optional? true})]
    {:resolve {:query [{:contact/of-current-user
                        [:contact/uuid
                         :contact/full-name
                         :contact/email
                         :contact/github-login
                         :contact/linkedin-url
                         :contact/org-mailing-namespace
                         (? {:contact/type [:db/ident]})
                         (? {:contact/humble-user [:user/uuid (? :user/full-name)]})]}]
               :store-in [:timeline/contact-directory-raw]
               :on-success-fx [[:dispatch [:timeline/contact-directory-loaded]]]}}))

;; The timeline filter "+" action: the Add-contact modal, prefilled from a
;; virtual contact.
(defevent-fx :contacts/create-from-virtual [_ {:keys [raw-id label sources contact domain]}]
  (let [email (when (and raw-id (some #{:gcal :linear :other} sources)
                         (str/includes? (or raw-id "") "@"))
                raw-id)
        gh    (when (and raw-id (contains? sources :github)) raw-id)
        initial (cond-> {}
                  contact                    (merge (select-keys contact
                                                                 [:contact/full-name
                                                                  :contact/email
                                                                  :contact/github-login
                                                                  :contact/linkedin-url
                                                                  :contact/org-mailing-namespace]))
                  (and (not contact) domain) (assoc :contact/type :contact.type/organisation
                                                    :contact/org-mailing-namespace [domain]
                                                    :contact/full-name (or label domain))
                  (and (not contact) (not domain) email) (assoc :contact/email [email])
                  (and (not contact) (not domain) gh)    (assoc :contact/github-login gh)
                  (and (not contact) (not domain) label
                       (not= label raw-id))  (assoc :contact/full-name label))]
    {:fx [[:dispatch [::create/open initial]]]}))

(defevent :timeline/contact-directory-loaded [db _]
  (let [raw (get-in db [:timeline/contact-directory-raw :contact/of-current-user] [])
        contacts-by-identifier
        (reduce (fn [idx {:contact/keys [email github-login] :as contact}]
                  (cond-> (reduce #(assoc %1 (str/lower-case %2) contact) idx (or email []))
                    github-login (assoc (str/lower-case github-login) contact)))
                {}
                raw)
        ;; Extend the existing canonical-index so filter selection on a
        ;; "contact:<uuid>" row expands to all its identifiers.
        contact-canonical
        (reduce (fn [idx {:contact/keys [uuid email github-login]}]
                  (assoc idx (str "contact:" uuid)
                         (cond-> (into #{} (map str/lower-case) (or email []))
                           github-login (conj (str/lower-case github-login)))))
                {}
                raw)
        org? (fn [c] (= :contact.type/organisation (-> c :contact/type :db/ident)))
        org-contacts (filter org? raw)
        contacts-by-domain
        (reduce (fn [idx {:contact/keys [org-mailing-namespace] :as contact}]
                  (reduce (fn [idx* d]
                            (assoc idx* (str/lower-case d) contact))
                          idx
                          (or org-mailing-namespace [])))
                {}
                org-contacts)
        domain-canonical-index
        (reduce (fn [idx {:contact/keys [uuid org-mailing-namespace]}]
                  (assoc idx (str "contact:" uuid)
                         (into #{} (map str/lower-case) (or org-mailing-namespace []))))
                {}
                org-contacts)]
    (-> db
        (assoc :timeline/contact-directory raw
               :timeline/contacts-by-identifier contacts-by-identifier
               :timeline/contacts-by-domain contacts-by-domain
               :timeline/domain-canonical-index domain-canonical-index
               :timeline/org-contacts (vec org-contacts))
        (update :timeline/people-canonical-index merge contact-canonical)
        (dissoc :timeline/contact-directory-raw))))

;; ---------------------------------------------------------------------------
;; Filter events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/set-page-filter [{:keys [db]} [path value]]
  (let [db (assoc-in db (into [:timeline/page-filters] (if (vector? path) path [path])) value)]
    {:db db
     :fx [[:dispatch [:timeline/load-entries]]
          [:dispatch [:timeline/auto-save-filters]]]}))

(defevent-fx :timeline/auto-save-filters [{:keys [db]} _]
  ;; Debounced: cancel previous timeout, schedule new one in 500ms
  (when-let [h @auto-save-handle]
    (js/clearTimeout h))
  (let [h (js/setTimeout #(>e [:timeline/do-save-filters]) 500)]
    (reset! auto-save-handle h)
    {:db db}))

(defevent-fx :timeline/do-save-filters [{:keys [db]} _]
  ;; Persist both filter dimensions in one blob. Entity-filter "me" is stored
  ;; as the :__me__ sentinel (see select-me), so it survives id churn across
  ;; sessions and re-resolves to the current user at apply time.
  (let [filters {:page-filters  (get db :timeline/page-filters {})
                 :entity-filter (get db :timeline/entity-filter {:column nil :selected #{}})}]
    {:resolve {:query [{(list 'integration/save-filters
                              {:filters filters})
                        [:user/uuid]}]
               :store-in [:timeline/save-result]}}))

;; ---------------------------------------------------------------------------
;; Infinite scroll (load-more) events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/load-more [{:keys [db]} _]
  (let [loading?    (get db :timeline/loading-more? false)
        all-loaded? (get db :timeline/all-loaded? false)
        cursor      (get db :timeline/load-more-cursor)
        preset      (get-in db [:timeline/page-filters :date-range :preset] :all-time)]
    (if (or loading? all-loaded? (not= preset :all-time) (nil? cursor))
      {:db db}
      (let [page-filters (get db :timeline/page-filters {})
            since        (let [d (js/Date. (.getTime cursor))]
                           (.setDate d (- (.getDate d) all-time-chunk-days))
                           d)
            until        cursor
            page-param   (cond-> {}
                           (seq (:shown-types page-filters))
                           (assoc :shown-types (:shown-types page-filters))
                           true (assoc-in [:date-range :since] since)
                           true (assoc-in [:date-range :until] until))
            prev-id      (:timeline/load-more-request-id db)
            req-id       (random-uuid)]
        {:db (cond-> (-> db
                         (assoc :timeline/loading-more? true
                                :timeline/load-more-request-id req-id
                                ;; Remember the :since we sent so the loaded
                                ;; handler can advance the cursor a fixed
                                ;; chunk-days even when the window comes
                                ;; back empty.
                                :timeline/load-more-pending-since since))
               prev-id (update :resolve/canceled-requests (fnil conj #{}) prev-id))
         :resolve {:request-id req-id
                   :query [{(list :integration/entry-view
                                  {:selected-integrations nil
                                   :filters {:page page-param}})
                            (journal-entry-query)}
                           :integration/has-more-history?]
                   :store-in [:timeline/more-entries-raw]
                   :on-success-fx [[:dispatch [:timeline/more-entries-loaded]]]
                   :on-error-fx [[:dispatch [:timeline/load-more-error]]]}}))))

(defevent-fx :timeline/more-entries-loaded [{:keys [db]} _]
  (let [raw            (get db :timeline/more-entries-raw {})
        new-entries    (get raw :integration/entry-view [])
        has-more?      (get raw :integration/has-more-history? false)
        existing       (get db :timeline/entries [])
        existing-uuids (set (map :journal.entry/uuid existing))
        deduped        (remove #(contains? existing-uuids (:journal.entry/uuid %)) new-entries)
        merged         (into existing deduped)
        ;; Advance the cursor by a fixed chunk regardless of returned data
        ;; so an empty window still moves us further back in time.
        new-cursor     (get db :timeline/load-more-pending-since)]
    {:db (-> db
             (dissoc :timeline/load-more-request-id
                     :timeline/load-more-pending-since
                     :timeline/more-entries-raw)
             (assoc :timeline/entries merged
                    :timeline/loading-more? false
                    :timeline/all-loaded? (not has-more?))
             (cond-> new-cursor (assoc :timeline/load-more-cursor new-cursor)))
     ;; Keep crawling while the backend has older entries AND either this round
     ;; added nothing new or we still haven't reached a viewport-filling count.
     ;; Backend's :has-more-history? flag is the real terminator: once it flips
     ;; to false we stop, instead of grinding to a hardcoded year-floor.
     :fx (when (and has-more? (or (empty? deduped) (< (count merged) min-initial-fill)))
           [[:dispatch [:timeline/load-more]]])}))

(defevent :timeline/load-more-error [db _]
  (-> db
      (dissoc :timeline/load-more-request-id)
      (assoc :timeline/loading-more? false)))

;; ---------------------------------------------------------------------------
;; Modal events
;; ---------------------------------------------------------------------------

(defevent :timeline/open-integrations-modal [db _]
  (assoc db :timeline/integrations-modal-open? true))

(defevent-fx :timeline/close-integrations-modal [{:keys [db]} _]
  {:db (dissoc db :timeline/integrations-modal-open?)
   :fx [[:dispatch [:timeline/load-available]]
        [:dispatch [:timeline/load-entries]]]})

;; ---------------------------------------------------------------------------
;; Connection status events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/load-connection-status [_ _]
  {:resolve {:query [{:integration/connection-status
                      [:integration/type
                       :integration/connected?
                       :integration/enabled?
                       {:integration/connections
                        [:connection/uuid
                         :connection/type
                         :connection/identity
                         :connection/api-key-invalid?
                         :connection/api-key-invalid-reason
                         :timeline-integration/uuid
                         :integration/enabled?]}]}]
             :store-in [:timeline/connection-status-raw]
             :on-success-fx [[:dispatch [:timeline/connection-status-loaded]]]}})

(defn- ->connection-row
  "Shapes one row of the `:integration/connections` resolver payload into the
  db-local connection-status shape consumed by the calendar picker, the
  connections table, and the broken-connection badges."
  [row]
  {:connection/uuid (:connection/uuid row)
   :connection/type (:connection/type row)
   :email           (:connection/identity row)
   :invalid?        (boolean (:connection/api-key-invalid? row))
   :invalid-reason  (:connection/api-key-invalid-reason row)
   :ti-uuid         (:timeline-integration/uuid row)})

(defn- ->connection-status-entry [{:integration/keys [type connected? enabled? connections]}]
  [type {:connected?  connected?
         :enabled?    enabled?
         :connections (mapv ->connection-row connections)}])

(defevent-fx :timeline/connection-status-loaded [{:keys [db]} _]
  (let [raw (get db :timeline/connection-status-raw)
        connections (:integration/connection-status raw)]
    {:db (-> db
             (assoc :timeline/connection-status
                    (into {} (map ->connection-status-entry) connections))
             (dissoc :timeline/connection-status-raw))}))

;; ---------------------------------------------------------------------------
;; Not-connected / add integration events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/add-integration-by-type [{:keys [db]} integration-type]
  (let [status        (get-in db [:timeline/connection-status integration-type])
        backfill-days (reg/default-backfill-days integration-type)]
    (if (not (:connected? status))
      {:db (assoc db :timeline/not-connected-type integration-type)}
      {:resolve {:query [{(list 'integration/setup
                                {:integration-type integration-type
                                 :backfill-days    backfill-days})
                          [:integration/enabled? :backfill/enqueued?]}]
                 :store-in [:timeline/manage-integration-result]
                 :on-success-fx [[:dispatch [:timeline/manage-integration-done]]
                                 [:dispatch [:timeline/connect-complete
                                             {:integration-type integration-type
                                              :backfill-days    backfill-days}]]]}})))

;; Calendar-picker "existing account" row: run setup targeted at ONE specific
;; connection-uuid rather than reusing whichever connection of the type
;; happens to exist — multi-calendar support means there can be several.
(defevent-fx :timeline/setup-connection [_ {:keys [integration-type connection-uuid]}]
  (let [backfill-days (reg/default-backfill-days integration-type)]
    {:resolve {:query [{(list 'integration/setup
                              {:integration-type integration-type
                               :connection/uuid  connection-uuid
                               :backfill-days    backfill-days})
                        [:integration/enabled? :backfill/enqueued?]}]
               :store-in [:timeline/manage-integration-result]
               :on-success-fx [[:dispatch [:timeline/manage-integration-done]]
                               [:dispatch [:timeline/connect-complete
                                           {:integration-type integration-type
                                            :backfill-days    backfill-days}]]]}}))

(defevent :timeline/close-not-connected [db _]
  (dissoc db :timeline/not-connected-type))

(defevent :timeline/open-calendar-picker [db _]
  (assoc db :timeline/calendar-picker-open? true))

(defevent :timeline/close-calendar-picker [db _]
  (dissoc db :timeline/calendar-picker-open?))

;; ---------------------------------------------------------------------------
;; Backfill events
;; ---------------------------------------------------------------------------

(defevent :timeline/open-backfill [db integration-type]
  (assoc db :timeline/backfill-type integration-type
         :timeline/backfill-open? true))

(defevent-fx :timeline/close-backfill [{:keys [db]} _]
  (some-> (get db :timeline/backfill-poll-handle) js/clearInterval)
  {:db (dissoc db
               :timeline/backfill-open?
               :timeline/backfill-type
               :timeline/backfill-mode
               :timeline/backfill-loading?
               :timeline/backfill-result
               :timeline/backfill-poll-handle)
   :fx [[:dispatch [:timeline/stop-backfill-poller]]
        [:dispatch [:timeline/close-integrations-modal]]]})

(defevent :timeline/set-backfill-days [db days]
  (assoc db :timeline/backfill-days days))

(defevent-fx :timeline/start-backfill [{:keys [db]} _]
  (let [days (get db :timeline/backfill-days 30)
        integration-type (get db :timeline/backfill-type)]
    {:db (assoc db :timeline/backfill-loading? true
                :timeline/backfill-result nil)
     :resolve (case integration-type
                :timeline-integration.type/linear
                {:query [{(list 'integration/backfill-linear {:days days})
                          [:backfill/enqueued?]}]
                 :store-in [:timeline/backfill-mutation-result]
                 :transform-fn (fn [resp] (get resp 'integration/backfill-linear))
                 :on-success-fx [[:dispatch [:timeline/backfill-enqueued]]]
                 :on-error-fx [[:dispatch [:timeline/backfill-error]]]}
                :timeline-integration.type/github
                {:query [{(list 'integration/backfill-github {:days days})
                          [:backfill/enqueued?]}]
                 :store-in [:timeline/backfill-mutation-result]
                 :transform-fn (fn [resp] (get resp 'integration/backfill-github))
                 :on-success-fx [[:dispatch [:timeline/backfill-enqueued]]]
                 :on-error-fx [[:dispatch [:timeline/backfill-error]]]}
                :timeline-integration.type/google-calendar
                {:query [{(list 'integration/backfill-google-calendar {:days days})
                          [:backfill/enqueued?]}]
                 :store-in [:timeline/backfill-mutation-result]
                 :transform-fn (fn [resp] (get resp 'integration/backfill-google-calendar))
                 :on-success-fx [[:dispatch [:timeline/gcal-backfill-done]]]
                 :on-error-fx [[:dispatch [:timeline/backfill-error]]]}
                :timeline-integration.type/outlook-calendar
                {:query [{(list 'integration/backfill-outlook-calendar {:days days})
                          [:backfill/enqueued?]}]
                 :store-in [:timeline/backfill-mutation-result]
                 :transform-fn (fn [resp] (get resp 'integration/backfill-outlook-calendar))
                 :on-success-fx [[:dispatch [:timeline/gcal-backfill-done]]]
                 :on-error-fx [[:dispatch [:timeline/backfill-error]]]})}))

(defevent-fx :timeline/gcal-backfill-done [{:keys [db]} _]
  {:db (assoc db
              :timeline/backfill-loading? false
              :timeline/backfill-result "Import complete!")
   :fx [[:dispatch [:timeline/load-entries]]]})

(def ^:private integrations-table-id
  :hai.journal2.collection/integrations-table)

(defn- resolve-integrations-table-fx
  "`:fx` entries that refresh the integrations table — but only when it's
   actually mounted (registered under `:collections`). The connect/backfill
   flow runs on views that don't render the table (notably mobile), where
   resolving a bare table id throws \"No table found\" (collection/utils
   `->table`). Returns `nil` when the table isn't on screen, so `into` is a
   no-op there."
  [db]
  (when (get-in db [:collections integrations-table-id])
    [[:dispatch [:table/resolve integrations-table-id]]]))

(defevent-fx :timeline/backfill-enqueued [{:keys [db]} _]
  (let [enqueued? (get-in db [:timeline/backfill-mutation-result :backfill/enqueued?])]
    (if enqueued?
      (let [handle (js/setInterval #(>e [:timeline/poll-backfill]) 3000)]
        {:db (assoc db
                    :timeline/backfill-poll-handle handle
                    :timeline/backfill-in-progress? true)
         :fx (into [[:dispatch [:timeline/start-backfill-poller]]]
                   (resolve-integrations-table-fx db))})
      {:db (assoc db
                  :timeline/backfill-loading? false
                  :timeline/backfill-result "No connection found.")})))

(defevent-fx :timeline/poll-backfill [_ _]
  {:resolve {:query [:integration/backfill-in-progress?]
             :store-in [:timeline/backfill-poll-result]
             :on-success-fx [[:dispatch [:timeline/poll-backfill-done]]]
             :on-error-fx [[:dispatch [:timeline/backfill-error]]]}})

(defevent-fx :timeline/poll-backfill-done [{:keys [db]} _]
  (let [in-progress? (get-in db [:timeline/backfill-poll-result :integration/backfill-in-progress?])]
    (if in-progress?
      {:db db}
      {:db db
       :fx [[:dispatch [:timeline/backfill-complete]]]})))

(defevent :timeline/backfill-error [db _]
  (some-> (get db :timeline/backfill-poll-handle) js/clearInterval)
  (assoc db
         :timeline/backfill-loading? false
         :timeline/backfill-result "Something went wrong. Please try again."
         :timeline/backfill-poll-handle nil))

(defevent-fx :timeline/backfill-complete [{:keys [db]} _]
  (some-> (get db :timeline/backfill-poll-handle) js/clearInterval)
  {:db (assoc db
              :timeline/backfill-loading? false
              :timeline/backfill-result "Import complete!"
              :timeline/backfill-poll-handle nil
              :timeline/backfill-in-progress? false)
   :fx (into [[:dispatch [:timeline/stop-backfill-poller]]
              [:dispatch [:timeline/load-entries]]]
             (resolve-integrations-table-fx db))})

;; ---------------------------------------------------------------------------
;; On-demand backfill poller
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/check-backfill-on-load [_ _]
  {:resolve {:query [:integration/backfill-in-progress?]
             :store-in [:timeline/backfill-poller-result]
             :on-success-fx [[:dispatch [:timeline/backfill-check-done]]]}})

(defevent-fx :timeline/backfill-check-done [{:keys [db]} _]
  (let [in-progress?     (get-in db [:timeline/backfill-poller-result :integration/backfill-in-progress?])
        was-in-progress? (get db :timeline/backfill-in-progress? false)
        monitor?         (= :monitor (get db :timeline/backfill-mode))
        modal-open?      (get db :timeline/integrations-modal-open? false)
        just-finished?   (and was-in-progress? (not in-progress?))]
    (if in-progress?
      {:db (assoc db :timeline/backfill-in-progress? true)
       :fx [[:dispatch [:timeline/start-backfill-poller]]]}
      (cond-> {:db (cond-> (assoc db :timeline/backfill-in-progress? false)
                     (and monitor? just-finished?)
                     (assoc :timeline/backfill-loading? false
                            :timeline/backfill-result   "Import complete!"))}
        was-in-progress?
        (assoc :fx (cond-> [[:dispatch [:timeline/stop-backfill-poller]]
                            [:dispatch [:timeline/load-entries]]]
                     modal-open?
                     (conj [:dispatch [:table/resolve :hai.journal2.collection/integrations-table]])
                     (and monitor? just-finished?)
                     (conj [:dispatch-later {:ms 1500 :dispatch [:timeline/close-backfill]}])))))))

(defevent-fx :timeline/connect-complete
  [{:keys [db]} {:keys [integration-type backfill-days]}]
  {:db (assoc db
              :timeline/backfill-type        integration-type
              :timeline/backfill-days        backfill-days
              :timeline/backfill-mode        :monitor
              :timeline/backfill-open?       true
              :timeline/backfill-loading?    true
              :timeline/backfill-result      nil
              :timeline/backfill-in-progress? true)
   :fx [[:dispatch [:routing/navigate :timelines/view]]
        [:dispatch [:timeline/start-backfill-poller]]]})

(defevent-fx :timeline/start-backfill-poller [{:keys [db]} _]
  ;; Clear-and-restart rather than no-op when a handle exists. The old guard let
  ;; a path set backfill-in-progress? true while skipping poller creation; if the
  ;; pre-existing poller was then cleared, the flag was left true with no live
  ;; poller to ever clear it — wedging the import modal open until a page reload.
  ;; Restarting guarantees a live poller whenever in-progress? is set true.
  (some-> (get db :timeline/backfill-poller-handle) js/clearInterval)
  (let [handle (js/setInterval #(>e [:timeline/backfill-poll-tick]) 3000)]
    {:db (assoc db :timeline/backfill-poller-handle handle)}))

(defevent-fx :timeline/backfill-poll-tick [_ _]
  {:resolve {:query [:integration/backfill-in-progress?]
             :store-in [:timeline/backfill-poller-result]
             :on-success-fx [[:dispatch [:timeline/backfill-check-done]]]}})

(defevent :timeline/stop-backfill-poller [db _]
  (some-> (get db :timeline/backfill-poller-handle) js/clearInterval)
  (dissoc db :timeline/backfill-poller-handle))

;; ---------------------------------------------------------------------------
;; Manage integrations modal events
;; ---------------------------------------------------------------------------

(defevent-fx :timeline/manage-integration-done [{:keys [db]} _]
  {:db (dissoc db :timeline/manage-integration-result)
   :fx (into [[:dispatch [:timeline/load-available]]]
             (resolve-integrations-table-fx db))})

;; ---------------------------------------------------------------------------
;; Teardown
;; ---------------------------------------------------------------------------

(defevent :timeline/teardown [db _]
  (some-> (get db :timeline/backfill-poll-handle) js/clearInterval)
  (some-> (get db :timeline/backfill-poller-handle) js/clearInterval)
  (when-let [h @auto-save-handle]
    (js/clearTimeout h)
    (reset! auto-save-handle nil))
  (dissoc db
          :timeline/entries :timeline/entries-raw
          :timeline/entries-request-id
          :timeline/load-more-request-id
          :timeline/available :timeline/available-raw
          :timeline/page-filters
          :timeline/integrations-modal-open?
          :timeline/entity-filter
          :timeline/loading? :timeline/error?
          :timeline/refreshing? :timeline/refresh-result
          :timeline/backfill-open? :timeline/backfill-type
          :timeline/backfill-days :timeline/backfill-loading?
          :timeline/backfill-result :timeline/backfill-poll-handle
          :timeline/backfill-in-progress? :timeline/backfill-poller-handle
          :timeline/backfill-poller-result
          :timeline/not-connected-type
          :timeline/connection-status :timeline/connection-status-raw
          :timeline/save-result
          :timeline/saved-filters-raw
          :timeline/load-more-cursor :timeline/loading-more?
          :timeline/load-more-pending-since
          :timeline/all-loaded? :timeline/more-entries-raw
          :timeline/people-directory :timeline/people-directory-raw
          :timeline/people-index :timeline/people-canonical-index))

(defevent :timeline/load-error [db _]
  (-> db
      (dissoc :timeline/entries-request-id)
      (assoc :timeline/error? true :timeline/loading? false)))

;; ---------------------------------------------------------------------------
;; Subscriptions
;; ---------------------------------------------------------------------------

(defsub :timeline/entries [db]
  (get db :timeline/entries []))

(defsub :timeline/available [db]
  (get db :timeline/available []))

(defsub :timeline/page-filters [db]
  (get db :timeline/page-filters
       {:shown-types reg/all-types
        :date-range {:preset :all-time}}))

(defsub :timeline/integrations-modal-open? [db]
  (get db :timeline/integrations-modal-open? false))

(defsub :timeline/loading? [db]
  (get db :timeline/loading? true))

(defsub :timeline/error? [db]
  (get db :timeline/error? false))

(defsub :timeline/refreshing? [db]
  (get db :timeline/refreshing? false))

(defsub :timeline/connection-status [db]
  (get db :timeline/connection-status {}))

(defsub :timeline/not-connected-type [db]
  (get db :timeline/not-connected-type))

(defsub :timeline/calendar-picker-open? [db]
  (get db :timeline/calendar-picker-open? false))

(defsub :timeline/backfill-open? [db]
  (get db :timeline/backfill-open? false))

(defsub :timeline/backfill-type [db]
  (get db :timeline/backfill-type))

(defsub :timeline/backfill-days [db]
  (get db :timeline/backfill-days 30))

(defsub :timeline/backfill-loading? [db]
  (get db :timeline/backfill-loading? false))

(defsub :timeline/backfill-result [db]
  (get db :timeline/backfill-result))

(defsub :timeline/backfill-in-progress? [db]
  (get db :timeline/backfill-in-progress? false))

(defsub :timeline/backfill-mode [db]
  (get db :timeline/backfill-mode))

;; People directory subscriptions
(defsub :timeline/people-index [db]
  (get db :timeline/people-index {}))

(defsub :timeline/people-canonical-index [db]
  (get db :timeline/people-canonical-index {}))

;; GitHub username prompt modal
(defsub :timeline/github-username-modal-open? [db]
  (get db :timeline/github-username-modal-open? false))

(defsub :timeline/github-username-input [db]
  (get db :timeline/github-username-input ""))

(defevent :timeline/open-github-username-modal [db _]
  (assoc db
         :timeline/github-username-modal-open? true
         :timeline/github-username-input ""))

(defevent :timeline/close-github-username-modal [db _]
  (dissoc db
          :timeline/github-username-modal-open?
          :timeline/github-username-input))

(defevent :timeline/set-github-username-input [db value]
  (assoc db :timeline/github-username-input value))

(defn- github-username [db]
  (:user-config.timeline.github/my-actions-filter-username (:auth/identity db)))

(defn- github-available? [db]
  (boolean (get-in db [:timeline/connection-status
                       :timeline-integration.type/github
                       :connected?])))

(defn- current-user-emails [db]
  (let [identity (:auth/identity db)
        emails (:user/email identity)
        token-email (get-in identity [:token/claims :email])]
    (cond
      (string? emails) [emails]
      (seq emails) emails
      (seq token-email) [token-email]
      :else [])))

(defn- current-user-person-id [db]
  (let [people-index (get db :timeline/people-index {})
        emails (map str/lower-case (current-user-emails db))]
    (or (some #(some-> (get people-index %) :id) emails)
        (first emails))))

(defsub :timeline/github-username [db]
  (github-username db))

(defsub :timeline/github-available? [db]
  (boolean (github-available? db)))

(defsub :timeline/current-user-person-id [db]
  (current-user-person-id db))

(defevent-fx :timeline/select-me [{:keys [db]} _]
  (let [ef (get db :timeline/entity-filter {:column nil :selected #{}})
        ;; Store "me" as a stable sentinel rather than a snapshot person-id, so
        ;; the selection survives reload/session and re-resolves to the current
        ;; user at apply time (see apply-entity-filter / current-user-person-id).
        selected? (and (= :people (:column ef))
                       (contains? (:selected ef) :__me__))
        prompt-github? (and (github-available? db)
                            (not selected?)
                            (not (some-> (github-username db) str/trim not-empty)))]
    {:db (cond-> db
           selected?
           (assoc :timeline/entity-filter {:column nil :selected #{}})
           (not selected?)
           (assoc :timeline/entity-filter {:column :people :selected #{:__me__}})
           prompt-github?
           (assoc :timeline/github-username-modal-open? true
                  :timeline/github-username-input ""))
     :fx [[:dispatch [:timeline/auto-save-filters]]]}))

(defevent-fx :timeline/save-github-username [{:keys [db]} _]
  (let [username (str/trim (get db :timeline/github-username-input ""))]
    (if (seq username)
      {:db (-> db
               (dissoc :timeline/github-username-modal-open?
                       :timeline/github-username-input)
               (assoc-in [:auth/identity :user-config.timeline.github/my-actions-filter-username]
                         username)
               (assoc :timeline/entity-filter {:column :people :selected #{:__me__}}))
       :fx [[:dispatch [:timeline/auto-save-filters]]]
       :resolve {:query `[(~'user/update
                           ~{:user/uuid (-> db :auth/identity :user/uuid)
                             :user-config.timeline.github/my-actions-filter-username username})]
                 :on-success-fx [[:dispatch [:timeline/load-people-directory]]
                                 [:dispatch [:timeline/load-entries]]]}}
      {:db db})))

;; ---------------------------------------------------------------------------
;; Infinite scroll subscriptions
;; ---------------------------------------------------------------------------

(defsub :timeline/loading-more? [db]
  (get db :timeline/loading-more? false))

(defsub :timeline/all-loaded? [db]
  (get db :timeline/all-loaded? false))

(defsub :timeline/pagination-active?
  [page-filters <- [:timeline/page-filters]]
  ;; Default to :all-time when no preset is set, matching every load path
  ;; (load-entries/load-more/reload-current-window). A saved-filter blob that
  ;; predates the date-range dimension carries only :shown-types, so without this
  ;; default the sub reads nil, the infinite-scroll sentinel never renders, and
  ;; scroll silently stops paginating even though loading itself works.
  (= :all-time (get-in page-filters [:date-range :preset] :all-time)))

;; True whenever the timeline is fetching anything the user should see a loader
;; for: initial load, infinite-scroll crawl, gcal refresh, or backfill. Keyed on
;; `(not all-loaded?)` rather than the per-round `loading-more?` toggle so it
;; stays continuously true across crawl rounds — the single bottom-of-list
;; spinner is mounted once and never blinks.
(defsub :timeline/busy?
  [loading?    <- [:timeline/loading?]
   refreshing? <- [:timeline/refreshing?]
   backfill?   <- [:timeline/backfill-in-progress?]
   pag-active? <- [:timeline/pagination-active?]
   all-loaded? <- [:timeline/all-loaded?]]
  (or loading?
      refreshing?
      backfill?
      (and pag-active? (not all-loaded?))))

(defsub :timeline/load-more-cursor [db]
  (get db :timeline/load-more-cursor))

;; ---------------------------------------------------------------------------
;; Derived subscriptions
;; ---------------------------------------------------------------------------

(defn- date-key
  "Returns a 'YYYY-MM-DD' string for an instant in the user's local timezone."
  [inst]
  (let [d (js/Date. inst)]
    (str (.getFullYear d) "-"
         (let [m (inc (.getMonth d))]
           (if (< m 10) (str "0" m) (str m)))
         "-"
         (let [day (.getDate d)]
           (if (< day 10) (str "0" day) (str day))))))

(defn- today-str []
  (date-key (js/Date.)))

(defn- yesterday-str []
  (let [d (js/Date.)]
    (.setDate d (dec (.getDate d)))
    (date-key d)))

(defn- day-label [date-str]
  (let [today (today-str)
        yesterday (yesterday-str)]
    (cond
      (= date-str today) "Today"
      (= date-str yesterday) "Yesterday"
      :else (fu/format (fu/parse-local-date date-str) "EEEE, MMM d"))))

(defn- notes-entry?
  [entry]
  (= :journal.entry.type/notes
     (get-in entry [:journal.entry/type :db/ident])))

(defn- collapse-notes-by-note
  "Within one day's entries, collapses multiple `:journal.entry.type/notes`
  rows that share a `:note/uuid` into a single row."
  [day-entries]
  (let [{notes true other false} (group-by notes-entry? day-entries)
        collapsed (for [[_note-uuid versions]
                        (group-by :journal.entry.notes/note-uuid notes)
                        :let [latest (apply max-key
                                            #(.getTime (:journal.entry/time %))
                                            versions)]]
                    (assoc latest :journal.entry.notes/edit-count (count versions)))]
    (vec (concat (or other []) collapsed))))

;; ---------------------------------------------------------------------------
;; Entity filter (Column B: domains / Column C: people — mutually exclusive)
;; ---------------------------------------------------------------------------

(defsub :timeline/entity-filter [db]
  (get db :timeline/entity-filter {:column nil :selected #{}}))

(defevent-fx :timeline/select-domain [{:keys [db]} domain]
  (let [ef (get db :timeline/entity-filter {:column nil :selected #{}})
        selected (if (= :domain (:column ef))
                   (:selected ef)
                   #{})
        updated (if (contains? selected domain)
                  (disj selected domain)
                  (conj selected domain))
        new-ef (if (empty? updated)
                 {:column nil :selected #{}}
                 {:column :domain :selected updated})]
    {:db (assoc db :timeline/entity-filter new-ef)
     :fx [[:dispatch [:timeline/auto-save-filters]]]}))

(defevent-fx :timeline/select-person [{:keys [db]} person-id]
  (let [ef (get db :timeline/entity-filter {:column nil :selected #{}})
        selected (if (= :people (:column ef))
                   (:selected ef)
                   #{})
        updated (if (contains? selected person-id)
                  (disj selected person-id)
                  (conj selected person-id))
        new-ef (if (empty? updated)
                 {:column nil :selected #{}}
                 {:column :people :selected updated})]
    {:db (assoc db :timeline/entity-filter new-ef)
     :fx [[:dispatch [:timeline/auto-save-filters]]]}))

(defevent-fx :timeline/clear-entity-filter [{:keys [db]} _]
  {:db (assoc db :timeline/entity-filter {:column nil :selected #{}})
   :fx [[:dispatch [:timeline/auto-save-filters]]]})

;; ---------------------------------------------------------------------------
;; Domain/people extraction helpers
;; ---------------------------------------------------------------------------

(defn- domain-from-email [email]
  (when (and email (str/includes? email "@"))
    (subs email (inc (str/index-of email "@")))))

(defn- extract-domains-from-entry [entry]
  (let [t (get-in entry [:journal.entry/type :db/ident])]
    (case t
      :journal.entry.type/google-calendar
      (keep domain-from-email (:journal.entry.google-calendar/attendees entry))

      :journal.entry.type/outlook-calendar
      (keep domain-from-email (:journal.entry.outlook-calendar/attendees entry))

      :journal.entry.type/linear
      (keep domain-from-email [(:journal.entry.linear/actor-email entry)
                               (:journal.entry.linear/creator-email entry)
                               (:journal.entry.linear/assignee-email entry)])

      :journal.entry.type/github
      (keep domain-from-email
            [(:journal.entry.github/actor-email entry)
             (:journal.entry.github/author-email entry)
             (:journal.entry.github/reviewer-email entry)])

      ;; slack-dm, notes — domain from the sharer's email (creator-email);
      ;; author-display-name kept as a fallback for slack (a name yields nil).
      (keep domain-from-email
            [(:journal.entry/creator-email entry)
             (:journal.entry/author-display-name entry)]))))

(defn- distinct-by
  "Returns a transducer that removes duplicates by (f item)."
  [f]
  (fn [rf]
    (let [seen (volatile! #{})]
      (fn
        ([] (rf))
        ([result] (rf result))
        ([result input]
         (let [k (f input)]
           (if (contains? @seen k)
             result
             (do (vswap! seen conj k)
                 (rf result input)))))))))

(defn- person-label
  "Display label for a person: just name if available, else raw id."
  [{:keys [id name]}]
  (if (seq name) name id))

(defn- resolve-identifier
  "Resolve an identifier (email or github login) through the people directory.
  Returns a canonical person map or nil."
  [people-index identifier]
  (when (seq (str identifier))
    (get people-index (str/lower-case identifier))))

(defn- contact-display-name
  "Tiered display name for a contact:
   :contact/full-name → linked humble user's name → first email → github-login."
  [{:contact/keys [full-name humble-user email github-login]}]
  (or (not-empty full-name)
      (some-> humble-user :user/full-name not-empty)
      (some-> email first not-empty)
      github-login))

(defn- make-person-entry
  "Build a filter entry from a raw identifier. Resolution preference:
   1. The user's contact book (real :contact/* row) → tag :contact? true.
   2. The humble-user people directory → tag :directory? true.
   3. Raw identifier fallback (auto-discovered, no link to anything yet)."
  [people-index contacts-by-identifier raw-id source]
  (when (seq (str raw-id))
    (let [lc (str/lower-case raw-id)]
      (if-let [contact (get contacts-by-identifier lc)]
        {:id (str "contact:" (:contact/uuid contact))
         :label (contact-display-name contact)
         :contact? true
         :contact contact
         :sources #{source}}
        (if-let [person (resolve-identifier people-index raw-id)]
          {:id (:id person) :label (person-label person)
           :directory? true :contact? false
           :sources #{source}}
          {:id raw-id
           :label (if (= source :github)
                    (str "@" raw-id)
                    raw-id)
           :contact? false
           :raw-id raw-id
           :sources #{source}})))))

(defn- make-domain-entry
  "Build a canonical Organizations-filter entry from a raw domain string.
   Resolution preference:
   1. Org contact whose :contact/org-mailing-namespace includes the domain → :contact? true.
   2. Raw domain fallback (auto-discovered)."
  [contacts-by-domain raw-domain]
  (when (seq (str raw-domain))
    (let [lc (str/lower-case raw-domain)]
      (if-let [contact (get contacts-by-domain lc)]
        {:id (str "contact:" (:contact/uuid contact))
         :label (contact-display-name contact)
         :contact? true
         :contact contact
         :domain lc
         :sources #{:auto}}
        {:id lc
         :label lc
         :contact? false
         :domain lc
         :raw-id lc
         :sources #{:auto}}))))

(defn- org-contact-entry
  "Entry for an org contact that has no resolvable domain in journal entries.
   Rendered as searchable contact row but cannot filter (no domains to match)."
  [contact]
  {:id (str "contact:" (:contact/uuid contact))
   :label (contact-display-name contact)
   :contact? true
   :contact contact
   :no-domain? (empty? (:contact/org-mailing-namespace contact))
   :sources #{:auto}})

(defn- extract-people-from-entry [entry people-index contacts-by-identifier]
  (let [t (get-in entry [:journal.entry/type :db/ident])
        mk (fn [raw-id source]
             (make-person-entry people-index contacts-by-identifier raw-id source))]
    (case t
      :journal.entry.type/google-calendar
      (->> (:journal.entry.google-calendar/attendees entry)
           (keep #(mk % :gcal))
           vec)

      :journal.entry.type/outlook-calendar
      (->> (:journal.entry.outlook-calendar/attendees entry)
           (keep #(mk % :outlook))
           vec)

      :journal.entry.type/linear
      (->> [(:journal.entry.linear/actor-email entry)
            (:journal.entry.linear/creator-email entry)
            (:journal.entry.linear/assignee-email entry)]
           (keep #(mk % :linear))
           (into [] (distinct-by :id)))

      :journal.entry.type/github
      (let [pairs [[(get entry :journal.entry.github/actor-login)
                    (get entry :journal.entry.github/actor-email)]
                   [(get entry :journal.entry.github/author-login)
                    (get entry :journal.entry.github/author-email)]
                   [(get entry :journal.entry.github/reviewer-login)
                    (get entry :journal.entry.github/reviewer-email)]]]
        (->> pairs
             (keep (fn [[login email]]
                     (when (or login email)
                       (or (mk email :github)
                           (mk login :github)))))
             (into [] (distinct-by :id))))

      ;; slack-dm, notes — use creator email from integration
      (when-let [email (:journal.entry/creator-email entry)]
        (when-let [p (mk email :other)]
          [p])))))

;; ---------------------------------------------------------------------------
;; Available domains/people derived subs
;; ---------------------------------------------------------------------------

(defsub :timeline/contacts-by-domain [db]
  (get db :timeline/contacts-by-domain {}))

(defsub :timeline/domain-canonical-index [db]
  (get db :timeline/domain-canonical-index {}))

(defsub :timeline/org-contacts [db]
  (get db :timeline/org-contacts []))

(defsub :timeline/available-domains
  [entries <- [:timeline/entries]
   contacts-by-domain <- [:timeline/contacts-by-domain]
   org-contacts <- [:timeline/org-contacts]]
  (let [from-entries (->> entries
                          (mapcat extract-domains-from-entry)
                          (remove nil?)
                          (keep #(make-domain-entry contacts-by-domain %)))
        from-contact-domains (keep #(make-domain-entry contacts-by-domain %)
                                   (keys contacts-by-domain))
        zero-domain-orgs (->> org-contacts
                              (filter #(empty? (:contact/org-mailing-namespace %)))
                              (map org-contact-entry))]
    (->> (concat from-entries from-contact-domains zero-domain-orgs)
         (reduce (fn [m {:keys [id] :as e}]
                   (if (contains? m id)
                     (update-in m [id :sources] (fnil into #{}) (:sources e))
                     (assoc m id e)))
                 {})
         vals
         (sort-by (juxt (complement :contact?) :label))
         vec)))

(defsub :timeline/contacts-by-identifier [db]
  (get db :timeline/contacts-by-identifier {}))

(defsub :timeline/available-people
  [entries <- [:timeline/entries]
   people-index <- [:timeline/people-index]
   contacts-by-identifier <- [:timeline/contacts-by-identifier]]
  (->> entries
       (mapcat #(extract-people-from-entry % people-index contacts-by-identifier))
       (remove nil?)
       (reduce (fn [m {:keys [id sources] :as p}]
                 (if (contains? m id)
                   (update-in m [id :sources] into sources)
                   (assoc m id p)))
               {})
       vals
       (sort-by (juxt (complement :contact?) :label))
       vec))

;; ---------------------------------------------------------------------------
;; Client-side entity filtering
;; ---------------------------------------------------------------------------

(defn- entry-matches-domain?
  "Checks if an entry mentions a domain, expanding contact-canonical IDs to all their org-mailing-namespace domains."
  [entry selected-id domain-canonical-index]
  (let [domains (into #{} (map str/lower-case) (extract-domains-from-entry entry))
        targets (get domain-canonical-index selected-id #{(some-> selected-id str/lower-case)})]
    (some #(contains? domains %) targets)))

(defn- entry-raw-identifiers
  "Extracts all raw identifier strings (emails + logins) from an entry."
  [entry]
  (let [t (get-in entry [:journal.entry/type :db/ident])]
    (case t
      :journal.entry.type/google-calendar
      (set (:journal.entry.google-calendar/attendees entry))

      :journal.entry.type/outlook-calendar
      (set (:journal.entry.outlook-calendar/attendees entry))

      :journal.entry.type/linear
      (into #{} (remove nil?)
            [(:journal.entry.linear/actor-email entry)
             (:journal.entry.linear/creator-email entry)
             (:journal.entry.linear/assignee-email entry)])

      :journal.entry.type/github
      (into #{} (remove nil?)
            [(get entry :journal.entry.github/actor-email)
             (get entry :journal.entry.github/actor-login)
             (get entry :journal.entry.github/author-email)
             (get entry :journal.entry.github/author-login)
             (get entry :journal.entry.github/reviewer-email)
             (get entry :journal.entry.github/reviewer-login)])

      (into #{} (remove nil?) [(:journal.entry/creator-email entry)]))))

(defn- entry-matches-person?
  "Checks if an entry mentions a person, expanding canonical ID to all known identifiers."
  [entry person-id canonical-index]
  (let [identifiers (get canonical-index person-id #{person-id})
        raw-ids (entry-raw-identifiers entry)]
    (some #(contains? identifiers (str/lower-case %)) raw-ids)))

(defn- apply-entity-filter [entries entity-filter canonical-index domain-canonical-index me-person-id]
  (let [{:keys [column selected]} entity-filter
        ;; Resolve the :__me__ sentinel to the live current-user person-id.
        selected (if (and (= :people column) (contains? selected :__me__))
                   (cond-> (disj selected :__me__)
                     me-person-id (conj me-person-id))
                   selected)]
    (if (or (nil? column) (empty? selected))
      entries
      (filterv
       (fn [entry]
         (let [t (get-in entry [:journal.entry/type :db/ident])]
           (if (#{:journal.entry.type/slack-dm} t)
             true
             (case column
               :domain (some #(entry-matches-domain? entry % domain-canonical-index) selected)
               :people (some #(entry-matches-person? entry % canonical-index) selected)
               true))))
       entries))))

;; ---------------------------------------------------------------------------
;; Filtered entries + entries-by-day
;; ---------------------------------------------------------------------------

(defsub :timeline/filtered-entries
  [entries <- [:timeline/entries]
   entity-filter <- [:timeline/entity-filter]
   canonical-index <- [:timeline/people-canonical-index]
   domain-canonical-index <- [:timeline/domain-canonical-index]
   me-person-id <- [:timeline/current-user-person-id]]
  (apply-entity-filter entries entity-filter canonical-index domain-canonical-index me-person-id))

(defsub :timeline/entries-by-day
  [entries <- [:timeline/filtered-entries]]
  (let [grouped (->> entries
                     (group-by #(date-key (:journal.entry/time %)))
                     (sort-by first #(compare %2 %1)))]
    (mapv (fn [[date-str day-entries]]
            {:date date-str
             :label (day-label date-str)
             :entries (->> day-entries
                           collapse-notes-by-note
                           (sort-by :journal.entry/time #(compare %2 %1))
                           vec)})
          grouped)))
